/* SMP Hero Carousel: arrows, dots, autoplay (pauses on hover/focus/hidden tab), swipe, keyboard, theme-editor aware. */
if (!customElements.get('smp-hero-carousel')) {
  class SmpHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.smp-hc__slide'));
      this.dots = Array.from(this.querySelectorAll('.smp-hc__dot'));
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (this.slides.length < 2) return;
      this.speed = parseInt(this.dataset.speed, 10) || 6000;
      this.autoplay =
        this.dataset.autoplay === 'true' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
        !window.Shopify?.designMode;
      this.paused = false;

      this.onClick = (e) => {
        const arrow = e.target.closest('.smp-hc__arrow');
        const dot = e.target.closest('.smp-hc__dot');
        if (arrow) this.go(this.index + Number(arrow.dataset.dir), true);
        else if (dot) this.go(Number(dot.dataset.go), true);
      };
      this.onKey = (e) => {
        if (e.key === 'ArrowLeft') { this.go(this.index - 1, true); e.preventDefault(); }
        if (e.key === 'ArrowRight') { this.go(this.index + 1, true); e.preventDefault(); }
      };
      this.onEnter = () => { this.paused = true; };
      this.onLeave = () => { this.paused = false; };
      this.onFocusOut = (e) => { if (!this.contains(e.relatedTarget)) this.paused = false; };
      this.onTouchStart = (e) => { this.touchX = e.touches[0].clientX; this.touchY = e.touches[0].clientY; };
      this.onTouchEnd = (e) => {
        if (this.touchX == null) return;
        const dx = e.changedTouches[0].clientX - this.touchX;
        const dy = e.changedTouches[0].clientY - this.touchY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1), true);
        this.touchX = null;
      };

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });

      // Theme editor: jump to the slide being edited.
      this.onBlockSelect = (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) this.go(i, true);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Warm the second slide only once the page itself has finished loading, so
      // it never competes with the first slide (the LCP image) for bandwidth.
      const warmNext = () => this.hydrate(this.slides[(this.index + 1) % this.slides.length]);
      if (document.readyState === 'complete') warmNext();
      else window.addEventListener('load', warmNext, { once: true });

      // Autoplay waits for the visitor's first scroll, tap, click or key press
      // (window.smpOnInteraction, snippets/load-after-interaction.liquid). Those are the inputs
      // after which the browser stops measuring LCP, so a rotated-in slide can never be
      // recorded as the LCP element (PageSpeed desktop runs recorded a later slide at ~7.5s
      // while slide 1 was on screen). Arrows, dots and swipe work immediately.
      const startAutoplay = () => { if (!this.timer) this.start(); };
      if (typeof window.smpOnInteraction === 'function') window.smpOnInteraction(startAutoplay);
      else if (document.readyState === 'complete') this.start();
      else window.addEventListener('load', startAutoplay, { once: true });
    }

    // Slides after the first ship with data-src/data-srcset only (see the section).
    hydrate(slide) {
      slide?.querySelectorAll('img[data-src]').forEach((img) => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    start() {
      clearInterval(this.timer);
      if (!this.autoplay) return;
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.go(this.index + 1, false);
      }, this.speed);
    }

    go(i, user) {
      const n = this.slides.length;
      const next = ((i % n) + n) % n;
      if (next === this.index) return;
      const prev = this.slides[this.index];
      const cur = this.slides[next];

      prev.classList.remove('is-active');
      prev.setAttribute('aria-hidden', 'true');
      prev.inert = true;
      prev.querySelectorAll('a.smp-hc__cta').forEach((a) => a.setAttribute('tabindex', '-1'));

      cur.classList.add('is-active');
      cur.removeAttribute('aria-hidden');
      cur.inert = false;
      cur.querySelectorAll('a.smp-hc__cta').forEach((a) => a.removeAttribute('tabindex'));
      this.hydrate(cur);

      this.dots.forEach((d, k) => {
        d.classList.toggle('is-active', k === next);
        if (k === next) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });

      this.index = next;
      // Preload the following slide's image so it's ready.
      this.hydrate(this.slides[(next + 1) % n]);
      if (user) this.start();
    }
  }
  customElements.define('smp-hero-carousel', SmpHeroCarousel);
}
